// src/components/CierreMes.jsx
import { useMemo, useState, useEffect } from "react";
import { descargarFacturasZip } from "../api";

const normCode = (s) => {
  const value = String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_\s]/g, "-")
    .trim();

  if (value === "psicologa-g") return "psicologia-g";
  if (value === "psicologa-m") return "psicologia-m";
  if (value === "psicologia-adultos") return "psicologia-adultos";
  if (value === "psicologia") return "psicologia";

  return value;
};

const mapEspecialidadAFiltro = (codeRaw) => normCode(codeRaw);

const getSpecialtyCode = (p) =>
  normCode(
    p.specialty_code ||
      p.specialty ||
      p.specialty_name ||
      ""
  );

const getProfessionalId = (p) => {
  const id = Number(p.professional_id);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const getProfessionalName = (p) =>
  String(p.professional_name || "").trim() || "Sin profesional asignado";

const getSpecialtyLabel = (p) =>
  p.specialty_name ||
  p.specialty_code ||
  p.specialty ||
  "Sin especialidad";

/*
 * Mantiene por ahora la lógica económica existente.
 * Si más adelante guardas el porcentaje por profesional en BD,
 * bastará con sustituir esta función.
 */
const calcularFactorCentro = (codeRaw) => {
  const code = normCode(codeRaw);

  if (code === "logopeda" || code === "logopedia" || code === "nutricion") {
    return 0.3;
  }

  if (
    code === "psicologia-g" ||
    code === "psicologia-m" ||
    code === "psicologia-adultos"
  ) {
    return 0.4;
  }

  if (code === "psicologia") {
    return 1;
  }

  return 1;
};

const calcularResumen = (items = []) => {
  let total = 0;
  let efectivo = 0;
  let tarjeta = 0;
  let centro = 0;
  let profesional = 0;

  items.forEach((p) => {
    const amt = Number(p.amount) || 0;
    const method = String(p.payment_method || "").toLowerCase().trim();
    const factorCentro = calcularFactorCentro(getSpecialtyCode(p));
    const parteCentro = amt * factorCentro;
    const parteProfesional = amt - parteCentro;

    total += amt;
    centro += parteCentro;
    profesional += parteProfesional;

    if (method === "efectivo") {
      efectivo += amt;
    } else {
      tarjeta += amt;
    }
  });

  return {
    total,
    efectivo,
    tarjeta,
    centro,
    profesional,
    count: items.length,
  };
};

export default function CierreMes({
  mes,
  setMes,
  pagos,
  resumen, // compatibilidad
  filtroEspecialidades,
  setFiltroEspecialidades,
  specialtiesOptions,
  loading,
  error,
}) {
  const [verTarjeta, setVerTarjeta] = useState(true);
  const [verEfectivo, setVerEfectivo] = useState(true);

  const [mostrarEspecialidades, setMostrarEspecialidades] = useState({});
  const [mostrarProfesionales, setMostrarProfesionales] = useState({});

  const [dlTarjeta, setDlTarjeta] = useState(true);
  const [dlEfectivo, setDlEfectivo] = useState(true);
  const [dlProfesionales, setDlProfesionales] = useState({});

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [downloadInfo, setDownloadInfo] = useState("");

  const profesionalesDisponibles = useMemo(() => {
    const map = new Map();

    (pagos || []).forEach((p) => {
      const id = getProfessionalId(p);
      const name = getProfessionalName(p);
      const key = id !== null ? String(id) : `sin-profesional-${name}`;

      if (!map.has(key)) {
        map.set(key, {
          key,
          id,
          name,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "es", { sensitivity: "base" })
    );
  }, [pagos]);

  useEffect(() => {
    setDlProfesionales((prev) => {
      const next = { ...prev };

      profesionalesDisponibles.forEach((p) => {
        if (next[p.key] === undefined) next[p.key] = true;
      });

      return next;
    });
  }, [profesionalesDisponibles]);

  useEffect(() => {
    setMostrarEspecialidades((prev) => {
      const next = { ...prev };

      (specialtiesOptions || []).forEach((s) => {
        const k = normCode(s.code);
        if (next[k] === undefined) next[k] = false;
      });

      return next;
    });
  }, [specialtiesOptions]);

  useEffect(() => {
    setMostrarProfesionales((prev) => {
      const next = { ...prev };

      profesionalesDisponibles.forEach((p) => {
        if (next[p.key] === undefined) next[p.key] = false;
      });

      return next;
    });
  }, [profesionalesDisponibles]);

  /*
   * Resumen general del centro:
   * siempre usa todos los pagos del mes.
   * No cambia al activar/desactivar checks de especialidad/profesional.
   */
  const resumenGeneralCentro = useMemo(() => {
    return calcularResumen(pagos || []);
  }, [pagos]);

  /*
   * Los checks de los desgloses son también los filtros de la tabla.
   * Si no hay ninguna especialidad o profesional marcado,
   * ese grupo no limita los resultados.
   */
  const pagosFiltrados = useMemo(() => {
    const especialidadesSeleccionadas = Object.entries(mostrarEspecialidades)
      .filter(([, checked]) => checked)
      .map(([code]) => code);

    const profesionalesSeleccionados = Object.entries(mostrarProfesionales)
      .filter(([, checked]) => checked)
      .map(([key]) => key);

    const hayFiltroEspecialidad = especialidadesSeleccionadas.length > 0;
    const hayFiltroProfesional = profesionalesSeleccionados.length > 0;

    return (pagos || []).filter((p) => {
      const code = mapEspecialidadAFiltro(
        p.specialty_code || p.specialty || p.specialty_name || ""
      );

      const method = String(p.payment_method || "").toLowerCase().trim();

      const pasaMetodo =
        (verTarjeta && method === "tarjeta") ||
        (verEfectivo && method === "efectivo") ||
        (!method && verTarjeta);

      const pasaEspecialidad =
        !hayFiltroEspecialidad ||
        especialidadesSeleccionadas.includes(code);

      const id = getProfessionalId(p);
      const name = getProfessionalName(p);
      const key = id !== null ? String(id) : `sin-profesional-${name}`;

      const pasaProfesional =
        !hayFiltroProfesional ||
        profesionalesSeleccionados.includes(key);

      return pasaMetodo && pasaEspecialidad && pasaProfesional;
    });
  }, [
    pagos,
    mostrarEspecialidades,
    mostrarProfesionales,
    verTarjeta,
    verEfectivo,
  ]);

  const resumenFiltrado = useMemo(
    () => calcularResumen(pagosFiltrados),
    [pagosFiltrados]
  );

  const resumenPorEspecialidad = useMemo(() => {
    const map = new Map();

    (pagos || []).forEach((p) => {
      const code = mapEspecialidadAFiltro(
        p.specialty_code || p.specialty || p.specialty_name || ""
      );

      if (!code) return;

      const label = getSpecialtyLabel(p);

      if (!map.has(code)) {
        map.set(code, {
          code,
          label,
          pagos: [],
        });
      }

      map.get(code).pagos.push(p);
    });

    return Array.from(map.values())
      .map((item) => ({
        ...item,
        resumen: calcularResumen(item.pagos),
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, "es", { sensitivity: "base" })
      );
  }, [pagos]);

  const resumenPorProfesional = useMemo(() => {
    const map = new Map();

    (pagos || []).forEach((p) => {
      const id = getProfessionalId(p);
      const name = getProfessionalName(p);
      const key = id !== null ? String(id) : `sin-profesional-${name}`;

      if (!map.has(key)) {
        map.set(key, {
          key,
          id,
          name,
          pagos: [],
        });
      }

      map.get(key).pagos.push(p);
    });

    return Array.from(map.values())
      .map((item) => ({
        ...item,
        resumen: calcularResumen(item.pagos),
      }))
      .sort((a, b) =>
        a.name.localeCompare(b.name, "es", { sensitivity: "base" })
      );
  }, [pagos]);

  const pagosParaDescarga = useMemo(() => {
    return (pagos || []).filter((p) => {
      const method = String(p.payment_method || "").toLowerCase().trim();

      const id = getProfessionalId(p);
      const name = getProfessionalName(p);
      const key = id !== null ? String(id) : `sin-profesional-${name}`;

      if (dlProfesionales[key] === false) return false;
      if (method === "tarjeta" && !dlTarjeta) return false;
      if (method === "efectivo" && !dlEfectivo) return false;

      return true;
    });
  }, [pagos, dlProfesionales, dlTarjeta, dlEfectivo]);

  const onDescargarZip = async () => {
    try {
      setDownloading(true);
      setDownloadError("");
      setDownloadInfo("");

      if (!mes) throw new Error("No hay mes seleccionado");

      const ids = (pagosParaDescarga || [])
        .map((p) => Number(p.id))
        .filter((x) => Number.isFinite(x) && x > 0);

      if (!ids.length) {
        throw new Error("No hay pagos para descargar");
      }

      await descargarFacturasZip({
        month: mes,
        paymentIds: ids,
      });

      setDownloadInfo(`ZIP generado con ${ids.length} pagos.`);
    } catch (e) {
      setDownloadError(e?.message || "Error generando ZIP");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="cierre-panel">
      <div className="pagos-actions">
        <div className="month-filter">
          <label>
            Mes:
            <input
              type="month"
              value={mes}
              onChange={(e) => setMes(e.target.value)}
            />
          </label>
        </div>
      </div>

      <div
        className="cierre-panel"
        style={{
          marginTop: 12,
          padding: 12,
          border: "1px solid #ddd",
          borderRadius: 10,
        }}
      >
        <strong>Método de pago</strong>

        <div
          style={{
            marginTop: 10,
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <label className="chk-especialidad">
            <input
              type="checkbox"
              checked={verTarjeta}
              onChange={(e) => setVerTarjeta(e.target.checked)}
            />
            💳 Tarjeta
          </label>

          <label className="chk-especialidad">
            <input
              type="checkbox"
              checked={verEfectivo}
              onChange={(e) => setVerEfectivo(e.target.checked)}
            />
            💵 Efectivo
          </label>
        </div>
      </div>

      <div
        className="cierre-panel"
        style={{
          marginTop: 12,
          padding: 12,
          border: "1px solid #ddd",
          borderRadius: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <strong>Descargar facturas del mes ({mes})</strong>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              Selecciona los profesionales y métodos de pago que quieres incluir en el ZIP.
            </div>
          </div>

          <button
            type="button"
            className="btn"
            onClick={onDescargarZip}
            disabled={
              downloading ||
              loading ||
              pagosParaDescarga.length === 0
            }
          >
            {downloading ? "Generando ZIP..." : "Descargar ZIP"}
          </button>
        </div>

        <div
          style={{
            marginTop: 10,
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <label className="chk-especialidad">
            <input
              type="checkbox"
              checked={dlTarjeta}
              onChange={(e) => setDlTarjeta(e.target.checked)}
            />
            💳 Tarjeta
          </label>

          <label className="chk-especialidad">
            <input
              type="checkbox"
              checked={dlEfectivo}
              onChange={(e) => setDlEfectivo(e.target.checked)}
            />
            💵 Efectivo
          </label>
        </div>

        <div
          style={{
            marginTop: 10,
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          {profesionalesDisponibles.map((p) => (
            <label key={`dl-pro-${p.key}`} className="chk-especialidad">
              <input
                type="checkbox"
                checked={dlProfesionales[p.key] !== false}
                onChange={(e) =>
                  setDlProfesionales((prev) => ({
                    ...prev,
                    [p.key]: e.target.checked,
                  }))
                }
              />
              {p.name}
            </label>
          ))}
        </div>

        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
          Facturas incluidas: <strong>{pagosParaDescarga.length}</strong>
        </div>

        {downloadInfo ? (
          <div style={{ marginTop: 8, fontSize: 12 }}>{downloadInfo}</div>
        ) : null}

        {downloadError ? (
          <div style={{ marginTop: 8, color: "red", whiteSpace: "pre-wrap" }}>
            {downloadError}
          </div>
        ) : null}
      </div>

      {loading ? (
        <p>Cargando cierre de mes...</p>
      ) : error ? (
        <p style={{ color: "red" }}>{error}</p>
      ) : (
        <>
          <h3 style={{ marginTop: 20 }}>Resumen general del centro</h3>

          <div className="cierre-resumen">
            <div className="cierre-card">
              <strong>Total mes:</strong>{" "}
              {resumenGeneralCentro.total.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Efectivo:</strong>{" "}
              {resumenGeneralCentro.efectivo.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Tarjeta:</strong>{" "}
              {resumenGeneralCentro.tarjeta.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Nº pagos:</strong>{" "}
              {resumenGeneralCentro.count}
            </div>

            <div className="cierre-card">
              <strong>Total profesionales:</strong>{" "}
              {resumenGeneralCentro.profesional.toFixed(2)} €
            </div>

            <div className="cierre-card cierre-card-destacado">
              <strong>Total real centro:</strong>{" "}
              {resumenGeneralCentro.centro.toFixed(2)} €
            </div>
          </div>

          <h3 style={{ marginTop: 24 }}>Desglose por especialidad</h3>

          <div className="especialidad-filter" style={{ marginBottom: 12 }}>
            {resumenPorEspecialidad.map((item) => (
              <label
                key={`res-spec-${item.code}`}
                className="chk-especialidad"
              >
                <input
                  type="checkbox"
                  checked={mostrarEspecialidades[item.code] === true}
                  onChange={(e) =>
                    setMostrarEspecialidades((prev) => ({
                      ...prev,
                      [item.code]: e.target.checked,
                    }))
                  }
                />
                {item.label}
              </label>
            ))}
          </div>

          {resumenPorEspecialidad
            .filter((item) => mostrarEspecialidades[item.code] === true)
            .map((item) => (
              <div
                key={`spec-card-${item.code}`}
                className="cierre-panel"
                style={{
                  marginTop: 10,
                  padding: 12,
                  border: "1px solid #ddd",
                  borderRadius: 10,
                }}
              >
                <strong>{item.label}</strong>

                <div className="cierre-resumen" style={{ marginTop: 10 }}>
                  <div className="cierre-card">
                    <strong>Facturado:</strong>{" "}
                    {item.resumen.total.toFixed(2)} €
                  </div>

                  <div className="cierre-card">
                    <strong>Tarjeta:</strong>{" "}
                    {item.resumen.tarjeta.toFixed(2)} €
                  </div>

                  <div className="cierre-card">
                    <strong>Efectivo:</strong>{" "}
                    {item.resumen.efectivo.toFixed(2)} €
                  </div>

                  <div className="cierre-card">
                    <strong>Centro:</strong>{" "}
                    {item.resumen.centro.toFixed(2)} €
                  </div>

                  <div className="cierre-card">
                    <strong>Profesionales:</strong>{" "}
                    {item.resumen.profesional.toFixed(2)} €
                  </div>

                  <div className="cierre-card">
                    <strong>Nº pagos:</strong>{" "}
                    {item.resumen.count}
                  </div>
                </div>
              </div>
            ))}

          <h3 style={{ marginTop: 24 }}>Desglose por profesional</h3>

          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            {resumenPorProfesional.map((item) => (
              <label key={`res-pro-${item.key}`} className="chk-especialidad">
                <input
                  type="checkbox"
                  checked={mostrarProfesionales[item.key] === true}
                  onChange={(e) =>
                    setMostrarProfesionales((prev) => ({
                      ...prev,
                      [item.key]: e.target.checked,
                    }))
                  }
                />
                {item.name}
              </label>
            ))}
          </div>

          {resumenPorProfesional
            .filter((item) => mostrarProfesionales[item.key] === true)
            .map((item) => (
              <div
                key={`pro-card-${item.key}`}
                className="cierre-panel"
                style={{
                  marginTop: 10,
                  padding: 12,
                  border: "1px solid #ddd",
                  borderRadius: 10,
                }}
              >
                <strong>{item.name}</strong>

                <div className="cierre-resumen" style={{ marginTop: 10 }}>
                  <div className="cierre-card">
                    <strong>Facturado:</strong>{" "}
                    {item.resumen.total.toFixed(2)} €
                  </div>

                  <div className="cierre-card">
                    <strong>Tarjeta:</strong>{" "}
                    {item.resumen.tarjeta.toFixed(2)} €
                  </div>

                  <div className="cierre-card">
                    <strong>Efectivo:</strong>{" "}
                    {item.resumen.efectivo.toFixed(2)} €
                  </div>

                  <div className="cierre-card">
                    <strong>Centro:</strong>{" "}
                    {item.resumen.centro.toFixed(2)} €
                  </div>

                  <div className="cierre-card">
                    <strong>Profesional:</strong>{" "}
                    {item.resumen.profesional.toFixed(2)} €
                  </div>

                  <div className="cierre-card">
                    <strong>Nº pagos:</strong>{" "}
                    {item.resumen.count}
                  </div>
                </div>
              </div>
            ))}

          <h3 style={{ marginTop: 24 }}>Pagos filtrados</h3>

          <div className="cierre-resumen" style={{ marginBottom: 12 }}>
            <div className="cierre-card">
              <strong>Total filtrado:</strong>{" "}
              {resumenFiltrado.total.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Nº pagos:</strong>{" "}
              {resumenFiltrado.count}
            </div>
          </div>

          {pagosFiltrados.length === 0 ? (
            <p>No hay pagos en este mes con los filtros seleccionados.</p>
          ) : (
            <table className="pv-table pagos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Paciente</th>
                  <th>Profesional</th>
                  <th>Especialidad</th>
                  <th>Servicio</th>
                  <th>Importe (€)</th>
                  <th>Método</th>
                </tr>
              </thead>

              <tbody>
                {pagosFiltrados.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {new Date(p.date).toLocaleDateString("es-ES")}
                    </td>

                    <td
                      className="paciente-cell"
                      title={`${p.paciente} ${p.apellidos || ""}`}
                    >
                      <strong>{p.paciente}</strong> {p.apellidos}
                    </td>

                    <td>{getProfessionalName(p)}</td>

                    <td>
                      {p.specialty_name ||
                        p.specialty_code ||
                        p.specialty ||
                        "—"}
                    </td>

                    <td>{p.service_name || "-"}</td>

                    <td style={{ textAlign: "center" }}>
                      {Number(p.amount || 0).toFixed(2)} €
                    </td>

                    <td>
                      {String(p.payment_method || "").toLowerCase() === "efectivo"
                        ? "💵 Efectivo"
                        : "💳 Tarjeta"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
